(() => {
  'use strict';

  if (window.__wk73CartController) return;
  window.__wk73CartController = true;

  const money = (cents) => new Intl.NumberFormat('nl-BE', {
    style: 'currency',
    currency: 'EUR'
  }).format((Number(cents) || 0) / 100);

  const boot = () => {
    const root = document.querySelector('[data-wk-cart-v73]');
    if (!root || !root.querySelector('[data-wk73-lines]')) return;

    const errorBox = root.querySelector('[data-wk73-cart-error]');
    const taxesIncluded = String(root.dataset.vatTaxesIncluded || 'false') !== 'false';
    let cart = null;
    let queue = Promise.resolve();
    let pending = 0;
    const desiredByKey = new Map();

    const showError = (message) => {
      if (!errorBox) return;
      errorBox.textContent = message || '';
      errorBox.hidden = !message;
    };

    const setBusy = (busy) => {
      root.setAttribute('aria-busy', busy ? 'true' : 'false');
    };

    const fetchCart = async () => {
      const response = await fetch('/cart.js', {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!response.ok) throw new Error('Cart kon niet worden geladen.');
      return response.json();
    };

    const emit = () => {
      if (!cart) return;
      window.dispatchEvent(new CustomEvent('wokora:cart-v73', {
        detail: { cart }
      }));
    };

    const vatRateForLine = (line) => {
      const raw = line?.dataset?.vatRate;
      if (raw === '' || raw == null) return null;
      const rate = Number(raw);
      return Number.isFinite(rate) && rate >= 0 ? rate : null;
    };

    const lineVatState = (item, line) => {
      const rate = vatRateForLine(line);
      const rawUnit = Number(item?.final_price) || 0;
      const rawTotal = Number(item?.final_line_price) || 0;

      if (rate == null) {
        return {
          known: false,
          rate: null,
          rawUnit,
          rawTotal,
          netUnit: taxesIncluded ? null : rawUnit,
          netTotal: taxesIncluded ? null : rawTotal,
          grossTotal: taxesIncluded ? rawTotal : null
        };
      }

      const divisor = 100 + rate;
      return {
        known: true,
        rate,
        rawUnit,
        rawTotal,
        netUnit: taxesIncluded ? Math.round(rawUnit * 100 / divisor) : rawUnit,
        netTotal: taxesIncluded ? Math.round(rawTotal * 100 / divisor) : rawTotal,
        grossTotal: taxesIncluded ? rawTotal : Math.round(rawTotal * divisor / 100)
      };
    };

    const cartVatState = () => {
      const rawSubtotal = Number(cart?.items_subtotal_price ?? cart?.total_price ?? 0);
      const linesByKey = new Map();
      root.querySelectorAll('[data-wk73-line]').forEach((line) => linesByKey.set(line.dataset.key, line));

      let known = Array.isArray(cart?.items) && cart.items.length > 0;
      let net = 0;
      let gross = 0;

      (cart?.items || []).forEach((item) => {
        const state = lineVatState(item, linesByKey.get(item.key));
        if (!state.known) {
          known = false;
          if (taxesIncluded) gross += state.rawTotal;
          else net += state.rawTotal;
          return;
        }
        net += state.netTotal;
        gross += state.grossTotal;
      });

      if (!known) {
        if (taxesIncluded) gross = rawSubtotal;
        else net = rawSubtotal;
      }

      return {
        known,
        raw: rawSubtotal,
        net,
        gross,
        tax: known ? Math.max(0, gross - net) : null
      };
    };

    const renderVatSummary = () => {
      const state = cartVatState();
      const net = root.querySelector('[data-vat-cart-net]');
      const tax = root.querySelector('[data-vat-cart-tax]');
      const gross = root.querySelector('[data-vat-cart-gross]');
      const status = root.querySelector('[data-vat-cart-status]');

      if (net) {
        net.textContent = state.known
          ? money(state.net)
          : taxesIncluded ? '—' : money(state.raw);
      }
      if (tax) tax.textContent = state.known ? money(state.tax) : 'Bij checkout';
      if (gross) {
        gross.textContent = state.known
          ? money(state.gross)
          : taxesIncluded ? money(state.raw) : 'Bij checkout';
      }
      if (status) {
        status.textContent = state.known
          ? 'BTW is berekend op basis van het tarief per product. Checkout blijft de definitieve fiscale berekening.'
          : 'Voor één of meer producten is het BTW-tarief nog niet bevestigd. Checkout bepaalt het definitieve BTW-bedrag.';
      }
    };

    const render = () => {
      if (!cart) return;
      const linesByKey = new Map((cart.items || []).map((item) => [item.key, item]));

      root.querySelectorAll('[data-wk73-line]').forEach((line) => {
        const item = linesByKey.get(line.dataset.key);
        if (!item) {
          line.remove();
          return;
        }

        const input = line.querySelector('[data-wk73-input]');
        const total = line.querySelector('[data-wk73-line-total]');
        const vatNote = line.querySelector('[data-vat-cart-line-note]');
        const unit = line.querySelector('[data-vat-cart-unit]');
        const desired = desiredByKey.get(item.key);
        const vat = lineVatState(item, line);

        if (input && document.activeElement !== input) {
          input.value = desired == null ? item.quantity : desired;
        }

        if (total) total.textContent = money(vat.known ? vat.netTotal : vat.rawTotal);

        if (unit) {
          const unitLabel = unit.dataset.unitLabel || 'stuk';
          unit.textContent = vat.known
            ? `${money(vat.netUnit)} per ${unitLabel} excl. BTW`
            : `${money(vat.rawUnit)} per ${unitLabel}${taxesIncluded ? ' incl. BTW' : ' excl. BTW'}`;
        }

        if (vatNote) {
          vatNote.textContent = vat.known
            ? `${money(vat.grossTotal)} incl. BTW · BTW ${vat.rate}%`
            : 'BTW-tarief wordt bij checkout bevestigd';
        }
      });

      const countText = root.querySelector('[data-wk73-count]');
      const countNumber = root.querySelector('[data-wk73-count-number]');

      if (countText) {
        countText.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'artikel' : 'artikelen'}`;
      }
      if (countNumber) countNumber.textContent = String(cart.item_count);

      if (cart.item_count === 0) {
        window.location.reload();
        return;
      }

      renderVatSummary();
      emit();
    };

    const changeLine = async (key, quantity) => {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Aantal kon niet worden aangepast.');
      }
      return data;
    };

    const enqueue = (line, key, quantity, removing = false) => {
      pending += 1;
      setBusy(true);
      showError('');
      line?.classList.add(removing ? 'is-removing' : 'is-updating');

      queue = queue
        .then(async () => {
          cart = await changeLine(key, quantity);
          render();
        })
        .catch((error) => {
          showError(error?.message || 'Er ging iets mis. Probeer opnieuw.');
          return fetchCart()
            .then((fresh) => {
              cart = fresh;
              render();
            })
            .catch(() => {});
        })
        .finally(() => {
          pending = Math.max(0, pending - 1);
          if (pending === 0) {
            setBusy(false);
            desiredByKey.clear();
            const itemsByKey = new Map((cart?.items || []).map((item) => [item.key, item]));
            root.querySelectorAll('[data-wk73-line]').forEach((currentLine) => {
              const currentItem = itemsByKey.get(currentLine.dataset.key);
              const currentInput = currentLine.querySelector('[data-wk73-input]');
              if (currentItem && currentInput && document.activeElement !== currentInput) {
                currentInput.value = currentItem.quantity;
              }
              currentLine.classList.remove('is-updating', 'is-removing');
            });
          }
        });

      return queue;
    };

    root.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wk73-minus], [data-wk73-plus], [data-wk73-remove]');
      if (!button || !root.contains(button)) return;

      const line = button.closest('[data-wk73-line]');
      if (!line || line.classList.contains('is-removing')) return;

      const input = line.querySelector('[data-wk73-input]');
      const key = line.dataset.key;
      if (!input || !key) return;

      if (button.matches('[data-wk73-remove]')) {
        desiredByKey.set(key, 0);
        input.value = '0';
        enqueue(line, key, 0, true);
        return;
      }

      const queued = desiredByKey.get(key);
      const current = Math.max(1, queued == null ? (parseInt(input.value, 10) || 1) : queued);
      const next = button.matches('[data-wk73-plus]')
        ? current + 1
        : Math.max(1, current - 1);

      if (next === current) return;

      desiredByKey.set(key, next);
      input.value = String(next);
      enqueue(line, key, next);
    });

    root.addEventListener('change', (event) => {
      const input = event.target.closest('[data-wk73-input]');
      if (!input || !root.contains(input)) return;

      const line = input.closest('[data-wk73-line]');
      const key = line?.dataset.key;
      if (!line || !key) return;

      const quantity = Math.max(1, parseInt(input.value, 10) || 1);
      desiredByKey.set(key, quantity);
      input.value = String(quantity);
      enqueue(line, key, quantity);
    });

    root.addEventListener('click', async (event) => {
      const checkout = event.target.closest('[data-wk73-checkout]');
      if (!checkout || !root.contains(checkout)) return;

      if (root.dataset.wk73DeliveryReady !== 'true') {
        const delivery = root.querySelector('[data-wk73-delivery]');
        const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        delivery?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
        window.dispatchEvent(new CustomEvent('wokora:delivery-v73-attention'));
        return;
      }

      checkout.disabled = true;
      await queue.catch(() => {});
      window.location.assign('/checkout');
    });

    fetchCart()
      .then((data) => {
        cart = data;
        render();
      })
      .catch((error) => showError(error?.message || 'Winkelwagen kon niet worden geladen.'));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();